import { FC, useMemo } from 'react'
import { Spinner } from 'src/components/spinner'
import { SegmentedControl, SegmentedOption, ToggleGroup } from 'src/components/segmented-control'
import { SurveyQuestionOption } from 'src/services/survey'
import { QuestionDisplayOptions } from 'src/utils/constants'

// Layout values from the new layout field
export const LayoutOptions = {
  VERTICAL: 'VERTICAL',
  HORIZONTAL: 'HORIZONTAL',
  GRID: 'GRID',
  NONE: 'NONE',
} as const

type GridSelectFieldVariant = 'light' | 'dark' | 'primary'
type GridSelectFieldProps = {
  options: SurveyQuestionOption[]
  variant?: GridSelectFieldVariant
  value: any
  translate?: string | null
  loading?: boolean
  disabled?: boolean
  isMultiple?: boolean
  questionDisplay?: string | null
  layout?: string | null
  handleResponse: (value: any) => void
  handleSearch: (value: string) => void
}

// Each survey option's `value` is the response identity; fall back to its
// display text when a value isn't set so the segment still has a stable key.
const optionKey = (option: SurveyQuestionOption) => option.value ?? option.cleanDisplay

const GridSelectField: FC<GridSelectFieldProps> = ({
  options,
  value,
  handleResponse,
  disabled,
  loading,
  isMultiple,
  questionDisplay,
  layout,
}) => {
  const columns = useMemo(() => {
    // Prefer new layout field, fallback to questionDisplay for backward compatibility
    const effectiveLayout =
      layout && layout !== LayoutOptions.NONE
        ? layout
        : questionDisplay === QuestionDisplayOptions.LIST_VERTICAL
        ? LayoutOptions.VERTICAL
        : questionDisplay === QuestionDisplayOptions.LIST_HORIZONTAL
        ? LayoutOptions.HORIZONTAL
        : questionDisplay === QuestionDisplayOptions.GRID_LAYOUT
        ? LayoutOptions.GRID
        : LayoutOptions.GRID // default to grid

    if (effectiveLayout === LayoutOptions.VERTICAL) return 1
    if (effectiveLayout === LayoutOptions.HORIZONTAL) return Math.max(options.length, 1)
    return 3 // GRID or default
  }, [layout, questionDisplay, options.length])

  const segmentedOptions: SegmentedOption<string>[] = useMemo(
    () =>
      options.map((option) => ({
        value: optionKey(option) as string,
        label: option.cleanDisplay,
      })),
    [options],
  )

  if (loading) {
    return (
      <div className="flex justify-center">
        <Spinner size="sm" />
      </div>
    )
  }

  const shared = {
    'aria-label': 'Options',
    disabled,
    layout: 'grid' as const,
    columns,
    className: 'mx-auto justify-center',
  }

  if (isMultiple) {
    const values: string[] = Array.isArray(value) ? value : []
    return (
      <ToggleGroup
        options={segmentedOptions}
        values={values}
        onChange={handleResponse}
        {...shared}
      />
    )
  }

  return (
    <SegmentedControl
      options={segmentedOptions}
      value={value ?? null}
      onChange={handleResponse}
      {...shared}
    />
  )
}

export default GridSelectField
